// Finishing step — outside services (anodize, heat treat, powder coat, etc).
//
// Used by every non-purchased part type. Storage path is
// `part.outsideServices[]` (OutsideService per crmTypes). Each row:
// type + vendor + setup cost + per-piece cost + supplier tax %.

import { NumberInput } from '@/components/NumberInput';
import type { StepProps } from '../stepTypes';
import type { OutsideService } from '@/shared/crmTypes';

const SERVICE_TYPES = [
  'Anodize',
  'Heat treat',
  'Powder coat',
  'Plating',
  'Paint',
  'Polish',
  'Black oxide',
  'Passivate',
  'Other',
];

export function FinishingStep({ part, onChange }: StepProps) {
  const rows = part.outsideServices ?? [];

  function updateRow(i: number, patch: Partial<OutsideService>) {
    const next = rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r));
    onChange({ outsideServices: next });
  }

  function addRow() {
    const next: OutsideService[] = [
      ...rows,
      {
        type: SERVICE_TYPES[0],
        vendor: '',
        setupCost: 0,
        perPieceCost: 0,
        supplierTaxPct: 0,
      },
    ];
    onChange({ outsideServices: next });
  }

  function removeRow(i: number) {
    onChange({ outsideServices: rows.filter((_, idx) => idx !== i) });
  }

  return (
    <div className="space-y-3">
      {rows.length === 0 && (
        <div className="text-sm text-text-mute italic border border-dashed border-line-dark rounded p-4 text-center">
          No finishing services yet. Skip if this part ships as-machined.
        </div>
      )}

      {rows.map((r, i) => (
        <div key={i} className="border border-line rounded p-3 bg-bg/30 space-y-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label={`Service #${i + 1}`}>
              <select
                value={r.type ?? ''}
                onChange={(e) => updateRow(i, { type: e.target.value })}
                className="w-full px-2 py-1.5 border border-line-dark rounded text-sm bg-paper focus:outline-none focus:border-orange"
              >
                {SERVICE_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Vendor">
              <input
                type="text"
                value={r.vendor ?? ''}
                onChange={(e) => updateRow(i, { vendor: e.target.value })}
                placeholder='e.g. "Industrial Hard Chrome"'
                className="w-full px-2 py-1.5 border border-line-dark rounded text-sm focus:outline-none focus:border-orange"
              />
            </Field>
          </div>
          <div className="grid grid-cols-3 gap-3 items-end">
            <Field label="Setup ($)">
              <NumberInput
                value={r.setupCost}
                onChange={(v) => updateRow(i, { setupCost: v })}
                step={1}
                unit="$"
                placeholder="25"
                numpadTitle="Setup cost"
              />
            </Field>
            <Field label="Per piece ($)">
              <NumberInput
                value={r.perPieceCost}
                onChange={(v) => updateRow(i, { perPieceCost: v })}
                step={1}
                unit="$"
                placeholder="3.75"
                numpadTitle="Per-piece cost"
              />
            </Field>
            <Field label="Supplier tax %">
              <NumberInput
                value={r.supplierTaxPct}
                onChange={(v) => updateRow(i, { supplierTaxPct: v })}
                step={1}
                unit="%"
                placeholder="0"
                numpadTitle="Supplier tax %"
              />
            </Field>
          </div>
          <div className="text-right">
            <button
              type="button"
              onClick={() => removeRow(i)}
              className="text-xs text-text-mute hover:text-orange py-1.5 px-2"
            >
              ✕ Remove
            </button>
          </div>
        </div>
      ))}

      <button
        type="button"
        onClick={addRow}
        className="w-full text-sm text-orange border border-dashed border-orange/40 rounded py-2 hover:bg-orange/5 font-semibold"
      >
        + Add finishing service
      </button>
    </div>
  );
}

interface FieldProps {
  label: string;
  children: React.ReactNode;
}

function Field({ label, children }: FieldProps) {
  return (
    <label className="block">
      <span className="block text-[10px] font-semibold text-text-mute uppercase tracking-wide mb-1">
        {label}
      </span>
      {children}
    </label>
  );
}
